// GpsHistory.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import DatePicker from 'react-datepicker';
import Skeleton from 'react-loading-skeleton';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { 
  Play, Pause, RotateCcw, User, Calendar, 
  MapPin, Navigation, Clock, Activity, Download 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Import CSS
import 'react-datepicker/dist/react-datepicker.css';
import 'react-loading-skeleton/dist/skeleton.css';
import 'leaflet/dist/leaflet.css';

// Import Assets for Leaflet (Fixed to be at the top)
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// --- CONFIGURATION ---

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Fix Leaflet Icon issues
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Custom Component to handle Map Panning during Replay
const MapRecenter = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.panTo(position);
  }, [position, map]);
  return null;
};

const GPSHistory = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState('John Doe');
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [gpsData, setGpsData] = useState([]);

  // Mock API Fetch
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      const sampleData = [
        { time: '08:00', lat: 51.505, lng: -0.09, speed: 0, stop: false },
        { time: '08:15', lat: 51.507, lng: -0.092, speed: 25, stop: false },
        { time: '08:30', lat: 51.51, lng: -0.1, speed: 0, stop: true },
        { time: '08:45', lat: 51.512, lng: -0.098, speed: 45, stop: false },
        { time: '09:00', lat: 51.5, lng: -0.08, speed: 30, stop: false },
        { time: '09:15', lat: 51.515, lng: -0.085, speed: 0, stop: true },
        { time: '09:30', lat: 51.52, lng: -0.11, speed: 55, stop: false },
        { time: '10:00', lat: 51.5, lng: -0.09, speed: 0, stop: false },
      ];
      setGpsData(sampleData);
      setCurrentIndex(0);
      setIsLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, [selectedDate, selectedEmployee]);

  // Replay Logic
  useEffect(() => {
    let timer;
    if (isPlaying && currentIndex < gpsData.length - 1) {
      timer = setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
      }, 1000 / playbackSpeed);
    } else if (currentIndex >= gpsData.length - 1) {
      setIsPlaying(false);
    }
    return () => clearTimeout(timer);
  }, [isPlaying, currentIndex, gpsData, playbackSpeed]);

  const stats = useMemo(() => ({
    distance: (gpsData.length * 1.2).toFixed(2),
    stops: gpsData.filter(p => p.stop).length,
    activeHours: "06h 12m"
  }), [gpsData]);

  const lineData = {
    labels: gpsData.map(p => p.time),
    datasets: [{
      label: 'Speed (km/h)',
      data: gpsData.map(p => p.speed),
      borderColor: '#3B82F6',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      fill: true,
      tension: 0.4,
      pointRadius: 4,
      pointBackgroundColor: '#3B82F6',
    }],
  };

  const pathPositions = gpsData.map(p => [p.lat, p.lng]);
  const animatedPath = pathPositions.slice(0, currentIndex + 1);
  const currentPos = pathPositions[currentIndex];

  return (
    <div className="p-8 bg-[#F8FAFC] min-h-screen font-['Roboto']">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-[700] font-['Poppins'] text-slate-900">GPS History</h1>
          <p className="text-sm text-slate-500 font-['Roboto']">Replay routes and analyze movement performance</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-slate-200 shadow-sm">
            <User size={14} className="text-slate-400" />
            <select 
              className="text-xs font-bold font-['Poppins'] bg-transparent outline-none"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <option>John Doe</option>
              <option>Sarah Smith</option>
            </select>
          </div>
          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-slate-200 shadow-sm">
            <Calendar size={14} className="text-slate-400" />
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              className="text-xs font-bold font-['Poppins'] bg-transparent outline-none w-24"
            />
          </div>
          <button className="flex items-center gap-2 bg-teal-500 text-white px-4 py-1.5 rounded-full text-xs font-bold hover:bg-teal-600 transition shadow-lg shadow-teal-100">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-8">
        {/* Map & Playback Controls */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          <div className="bg-white p-2 rounded-3xl border border-slate-100 shadow-sm overflow-hidden relative">
            {isLoading ? (
              <Skeleton height={450} borderRadius={24} />
            ) : (
              <div className="h-[450px] w-full rounded-2xl overflow-hidden">
                <MapContainer center={[51.505, -0.09]} zoom={13} style={{ height: '100%', width: '100%' }}>
                  <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
                  <Polyline positions={pathPositions} color="#E2E8F0" weight={3} dashArray="5, 10" />
                  <Polyline positions={animatedPath} color="#3B82F6" weight={5} />
                  
                  {/* Start Point Marker */}
                  <Marker position={pathPositions[0]}>
                    <Popup><span className="font-bold">Journey Start</span></Popup>
                  </Marker>
                  
                  {/* Animated Tracking Dot */}
                  {currentPos && (
                    <Marker position={currentPos} icon={L.divIcon({
                      className: 'custom-div-icon',
                      html: `<div class="w-4 h-4 bg-blue-600 border-2 border-white rounded-full shadow-lg animate-pulse"></div>`,
                    })}>
                      <MapRecenter position={currentPos} />
                    </Marker>
                  )}
                </MapContainer>
              </div>
            )}

            {/* Playback Control Bar */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-[90%] bg-white/90 backdrop-blur-md p-4 rounded-2xl border border-slate-100 shadow-xl flex items-center gap-6 z-[1000]">
              <button 
                onClick={() => setIsPlaying(!isPlaying)}
                className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
              >
                {isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
              </button>
              
              <button 
                onClick={() => { setIsPlaying(false); setCurrentIndex(0); }}
                className="p-3 text-slate-400 hover:text-slate-900 transition"
              >
                <RotateCcw size={18} />
              </button>

              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 transition-all duration-300" 
                  style={{ width: `${(currentIndex / (gpsData.length - 1)) * 100}%` }}
                />
              </div>

              <select 
                className="text-[10px] font-black bg-slate-50 border border-slate-100 px-2 py-1 rounded-lg outline-none"
                value={playbackSpeed}
                onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
              >
                <option value={0.5}>0.5x</option>
                <option value={1}>1.0x</option>
                <option value={2}>2.0x</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sidebar Stats & Analytics */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
          <div className="grid grid-cols-1 gap-4">
            {[
              { label: 'Total Distance', value: stats.distance, unit: 'km', icon: Navigation, color: 'text-blue-500' },
              { label: 'Total Stops', value: stats.stops, unit: 'Stops', icon: MapPin, color: 'text-purple-500' },
              { label: 'Active Hours', value: stats.activeHours, unit: 'Hours', icon: Clock, color: 'text-teal-500' }
            ].map((stat, i) => (
              <div key={i} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-slate-50 rounded-xl">
                  <stat.icon size={20} className={stat.color} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest font-['Poppins']">{stat.label}</p>
                  <h4 className="text-lg font-bold text-slate-900 font-['Poppins']">{stat.value} <span className="text-xs font-normal text-slate-400">{stat.unit}</span></h4>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2 font-['Poppins']">
              <Activity size={14} className="text-blue-500" /> Movement Timeline
            </h3>
            <div className="h-[250px]">
              {isLoading ? (
                <Skeleton height="100%" />
              ) : (
                <Line 
                  data={lineData} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: { 
                      y: { display: true, grid: { color: '#f8fafc' }, ticks: { font: { size: 10 } } }, 
                      x: { grid: { display: false }, ticks: { font: { size: 10 } } } 
                    }
                  }} 
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GPSHistory;